

















































































































import Vue from 'vue';
import Permissions from '@/common/permissions';
import { mapGetters } from 'vuex';
import { PayaConfig, Processor } from '@/common/types/processors.types';
import AchPaymentMethod from '@/common/components/AchPaymentMethod.vue';
import KeyBankPaymentMethod from '@/common/components/KeybankPaymentMethod.vue';
import {
  CreditMethod,
  PaymentMethod,
  PaymentType,
  PaymentTypeDisplay,
} from '@/common/types/payments.types';
import { VirtualTerminalMerchant } from '@/iso/views/merchants/merchants.types';
import CreditCardPaymentMethod from './CreditCardPaymentMethod.vue';

export default Vue.extend({
  props: {
    value: {
      type: Object as () => PaymentMethod,
      required: true,
    },
    processors: {
      type: Array,
      required: true,
    },
    isAutomated: {
      type: Boolean,
      required: false,
      default: false,
    },
    errors: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    validationPrefix: {
      type: String,
      required: false,
      default: '',
    },
    creditCardValidationPrefix: {
      type: String,
      required: false,
      default: '',
    },
    achValidationPrefix: {
      type: String,
      required: false,
      default: '',
    },
    isCustomerVault: {
      type: Boolean,
      required: false,
      default: false,
    },
    virtualTerminalMerchant: {
      type: Object as () => VirtualTerminalMerchant,
      required: false,
    },
    showPaymentTypeErrorModal: {
      type: Boolean,
      required: false,
      default: false,
    },
    isCvvRequired: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      // localValueForTest is to make tests based on computed properties pass
      // without computed is not reactive in tests
      dialog: false, // show dialog variable
      notAllowedPaymentMethod: '',
      localValueForTest: this.value as PaymentMethod,
      PaymentType,
      PaymentTypeDisplay,
    };
  },
  mounted() {
    if (this.currentUser) {
      this.changeDefaultCreditMethodIfCaptureDisallowed();
    }
  },
  watch: {
    currentUser() {
      this.changeDefaultCreditMethodIfCaptureDisallowed();
    },
    showPaymentTypeErrorModal() {
      this.dialog = this.showPaymentTypeErrorModal;
    },
  },
  computed: {
    ...mapGetters(['currentUser', 'currentUserCan', 'isCustomerElectronicCheckEnabled', 'isCustomerCashEnabled', 'isCustomerKeybankElectronicCheckEnabled']),
    showCreditPaymentMethod(): boolean {
      return this.hasCreditProcessor && this.value.type === PaymentType.CREDIT;
    },
    hasCreditProcessor(): boolean {
      return (this.processors as Processor[]).some((p) => p.supports === PaymentType.CREDIT);
    },
    showAchPaymentMethod(): boolean {
      return this.isCustomerElectronicCheckEnabled
          && this.hasAchProcessor
          && (this.value.type === PaymentType.ACH);
    },
    showKeybankPaymentMethod(): boolean {
      return this.isCustomerKeybankElectronicCheckEnabled
          && this.hasKeybankProcessor
          && (this.value.type === PaymentType.KEYBANK);
    },
    hasKeybankProcessor(): boolean {
      return (this.processors as Processor[]).some((p) => p.key === PaymentType.KEYBANK);
    },
    hasAchProcessor(): boolean {
      return (this.processors as Processor[]).some((p) => p.supports === PaymentType.ACH);
    },
    showCashPaymentMethod(): boolean {
      return this.isCustomerCashEnabled
          && this.hasCashProcessor
          && this.value.type === PaymentType.CASH;
    },
    hasCashProcessor(): boolean {
      return (
        !this.isAutomated
        && (this.processors as Processor[]).some((p) => p.supports === PaymentType.CASH)
      );
    },
    supportedProcessors(): Processor[] {
      let processorsFilteredByType = (this.processors as Processor[]).filter(
        (p) => p.supports === this.value.type,
      );
      if (this.showAchPaymentMethod) {
        processorsFilteredByType = processorsFilteredByType.filter(
          (processor) => (processor.config as PayaConfig).type === this.value.ach?.accountHolderType,
        );
      }
      if (this.showKeybankPaymentMethod) {
        processorsFilteredByType = (this.processors as Processor[]).filter(
          (processor) => processor.key === this.value.type,
        );
      }
      return processorsFilteredByType;
    },
    supportedPaymentTypes(): Object[] {
      const paymentTypes = [
        {
          display: PaymentTypeDisplay.CREDIT,
          value: PaymentType.CREDIT,
        },
      ];
      if (this.isCustomerElectronicCheckEnabled && this.hasAchProcessor) {
        paymentTypes.push({
          display: PaymentTypeDisplay.ACH,
          value: PaymentType.ACH,
        });
      }
      if (this.isCustomerCashEnabled && this.hasCashProcessor) {
        paymentTypes.push({
          display: PaymentTypeDisplay.CASH,
          value: PaymentType.CASH,
        });
      }
      if (this.isCustomerKeybankElectronicCheckEnabled && this.hasKeybankProcessor) {
        paymentTypes.push({
          display: PaymentTypeDisplay.KEYBANK,
          value: PaymentType.KEYBANK,
        });
      }
      return paymentTypes;
    },
  },
  methods: {
    isProcessorIdSupported(processorId: string) {
      const supportedIds = this.supportedProcessors.map((processor) => processor.id);
      return supportedIds.includes(processorId);
    },
    setProcessorId() {
      if ((this.value.type === PaymentType.ACH && !this.isCustomerElectronicCheckEnabled)
      || (this.value.type === PaymentType.KEYBANK && !this.isCustomerKeybankElectronicCheckEnabled)
      || (this.value.type === PaymentType.CASH && !this.isCustomerCashEnabled)) {
        this.value.processorId = undefined;
        this.dialog = true;
        switch (this.value.type) {
          case PaymentType.ACH:
            this.notAllowedPaymentMethod = PaymentTypeDisplay.ACH;
            break;
          case PaymentType.KEYBANK:
            this.notAllowedPaymentMethod = PaymentTypeDisplay.KEYBANK;
            break;
          default:
            this.notAllowedPaymentMethod = PaymentTypeDisplay.CASH;
            break;
        }
      } else {
        this.value.processorId = this.supportedProcessors.length > 0
          ? this.supportedProcessors[0].id : undefined;
      }
      if (this.value.processorId) {
        this.updateTerminal();
      }
    },
    changeDefaultCreditMethodIfCaptureDisallowed() {
      if (
        this.value.creditCard!.method === CreditMethod.AUTH_AND_CAPTURE
        && !this.currentUserCan(Permissions.RUN_CC_SALE)
      ) {
        this.value.creditCard!.method = CreditMethod.AUTH_ONLY;
      }

      this.setProcessorId();
    },
    checkCardType() {
      this.$emit('created');
    },
    updateTerminal() {
      const { processorId } = this.value;
      const filtered = this.processors.filter((item: any) => item.id === processorId);

      this.$emit('changeTerminal', filtered[0]);
    },
  },
  components: {
    AchPaymentMethod,
    CreditCardPaymentMethod,
    KeyBankPaymentMethod,
  },
});
